import { Box } from '@chakra-ui/react';
import { DocumentVersionFieldFragment } from '@client/graphql/__generated__/types';
import { debounce } from 'lodash';
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

interface FieldImagesFromPdfProps {
  pdfUrl: string;
  documentVersionFields: DocumentVersionFieldFragment[];
  onImagesLoaded: (
    images: {
      documentVersionField: DocumentVersionFieldFragment;
      image: string;
    }[]
  ) => void;
}

export const FieldImagesFromPdf: FC<FieldImagesFromPdfProps> = memo(
  function FieldImagesFromPdf(props) {
    const canvasRefs = useRef<{ [key: number]: HTMLCanvasElement }>({});
    const [images, setImages] = useState<
      {
        documentVersionField: DocumentVersionFieldFragment;
        image: string;
      }[]
    >([]);
    const [canvases, setCanvases] = useState<{
      [key: number]: HTMLCanvasElement;
    }>({});

    const pageNumbers = useMemo(() => {
      return [
        ...new Set(
          props.documentVersionFields.map((field) => field.pageNumber)
        ),
      ].sort();
    }, [props.documentVersionFields]);

    useEffect(() => {
      const context = Object.keys(canvases).reduce(
        (acc, key) => {
          const canvas = canvases[key as unknown as number];
          if (!canvas) return acc;

          acc[key] = canvas.getContext('2d', { willReadFrequently: true });

          return acc;
        },
        {} as { [key: number]: CanvasRenderingContext2D | null }
      );

      const images = props.documentVersionFields.map((dvf) => {
        const ctx = context[dvf.pageNumber || 0];
        if (!ctx) return null;

        const width = ctx.canvas.width;
        const height = ctx.canvas.height;

        const imageData = ctx.getImageData(
          dvf.x * width,
          dvf.y * height,
          dvf.width * width,
          dvf.height * height
        );

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imageData.width;
        tempCanvas.height = imageData.height;
        const tempCtx = tempCanvas.getContext('2d', {
          willReadFrequently: true,
        });
        if (!tempCtx) return null;

        tempCtx.putImageData(imageData, 0, 0);

        return {
          documentVersionField: dvf,
          image: tempCanvas.toDataURL('image/png'),
        };
      });

      setImages(images.filter((image) => image !== null));
      props.onImagesLoaded(images.filter((image) => image !== null));

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Object.keys(canvases).length]);

    const onRenderSuccess = useMemo(() => {
      return debounce(() => {
        setCanvases(canvasRefs.current);
      }, 100);
    }, [canvasRefs]);

    return (
      <Box>
        <Box height="1px" overflow="hidden" width="1px">
          {images.length === 0 ? (
            <Document file={props.pdfUrl} renderMode="canvas">
              {pageNumbers.map((pageNumber) => (
                <Page
                  key={pageNumber}
                  canvasRef={(ref) => {
                    if (ref) {
                      canvasRefs.current[pageNumber] = ref;
                    }
                  }}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={false}
                  renderForms={false}
                  renderTextLayer={false}
                  onRenderSuccess={onRenderSuccess}
                />
              ))}
            </Document>
          ) : null}
        </Box>
      </Box>
    );
  }
);
